import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '@/components/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CATEGORY_META, CATEGORY_ORDER, getGeolocation } from '@/lib/utils';
import { MapPin, Search, Coffee, Beer, Grape, Martini, Disc3, Wine, Flame, PartyPopper, LocateFixed, Loader2, X, Cigarette, Store, Clock, Globe } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AddressAutocompleteInput } from '@/components/AddressAutocompleteInput';
import { ThirrstyBadge } from '@/components/ThirrstyBadge';
import { useAuth } from '@/context/AuthContext';

const ICONS = { martini: Martini, 'disc-3': Disc3, wine: Wine, flame: Flame, coffee: Coffee, grape: Grape, beer: Beer, 'party-popper': PartyPopper, cigarette: Cigarette, store: Store };
const PAGE = 100;

function VenueRow({ venue, onClick }) {
  const catMeta = CATEGORY_META[venue.category];
  const Icon = catMeta ? (ICONS[catMeta.icon] || Martini) : Martini;
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-3.5 text-left transition-colors hover:border-[#F6B24A]/40"
      data-testid={`discover-venue-row-${venue.id}`}
    >
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/8 text-[#F6B24A]">
        <Icon className="h-7 w-7" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-base font-semibold text-white">
          <span className="truncate">{venue.name}</span>
          {venue.subscription_plan && <ThirrstyBadge size="xs" testId={`discover-thirrsty-badge-${venue.id}`} />}
        </p>
        <p className="mt-0.5 truncate text-sm text-white/60">
          {catMeta?.label || venue.category} &bull; {venue.city}, {venue.state}
          {typeof venue.distance_km === 'number' && ` \u2022 ${(venue.distance_km * 0.621371).toFixed(1)} mi`}
        </p>
        {venue.claim_status === 'unclaimed' ? (
          <span className="mt-1 inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/60">
            Unclaimed &middot; owner? Claim it
          </span>
        ) : venue.discount_text ? (
          <p className="mt-1 truncate text-xs text-[#F6B24A]"><MapPin className="mr-1 inline h-3 w-3" />{venue.discount_text}</p>
        ) : null}
      </div>
    </button>
  );
}

export default function DiscoverPage() {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [search, setSearch] = useState('');
  const [addressQuery, setAddressQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [coords, setCoords] = useState(null);
  const [locationLabel, setLocationLabel] = useState('');
  const [locating, setLocating] = useState(false);
  const [venues, setVenues] = useState([]);
  const [people, setPeople] = useState([]);
  const [followBusy, setFollowBusy] = useState(null);

  const toggleFollow = async (u) => {
    setFollowBusy(u.id);
    try {
      if (u.is_following) {
        await api.delete(`/follow/user/${u.id}`);
      } else {
        await api.post('/follow', { target_type: 'user', target_id: u.id });
      }
      setPeople((prev) => prev.map((p) => (p.id === u.id ? { ...p, is_following: !u.is_following } : p)));
      toast.success(u.is_following ? `Unfollowed ${u.display_name}` : `Following ${u.display_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update follow');
    } finally {
      setFollowBusy(null);
    }
  };
  const [regions, setRegions] = useState([]);
  const [trending, setTrending] = useState([]);
  const [country, setCountry] = useState('all');
  const [city, setCity] = useState('all');
  const [openNow, setOpenNow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const stateRef = useRef({});

  const trimmed = search.trim();
  const isZipLike = /^\d{3,5}$/.test(trimmed);
  const hasTextQuery = trimmed.length >= 2 && !isZipLike;

  const buildParams = useCallback((offset) => {
    const params = { limit: PAGE };
    if (hasTextQuery) params.q = trimmed;
    if (category !== 'all') params.category = category;
    if (country !== 'all') params.country = country;
    if (city !== 'all') params.city = city;
    if (openNow) params.open_now = true;
    if (coords) {
      params.lat = coords.lat;
      params.lon = coords.lon;
      params.limit = 200;
    } else {
      params.sort = 'name';
      params.offset = offset;
    }
    return params;
  }, [hasTextQuery, trimmed, category, country, city, openNow, coords]);

  const fetchPage = useCallback(async (reset) => {
    const offset = reset ? 0 : stateRef.current.offset || 0;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const r = await api.get('/venues', { params: buildParams(offset) });
      const batch = r.data || [];
      setVenues((prev) => (reset ? batch : [...prev, ...batch]));
      const paginated = !coords;
      stateRef.current.offset = offset + batch.length;
      setHasMore(paginated && batch.length === PAGE);
    } catch {
      if (reset) setVenues([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams, coords]);

  useEffect(() => {
    api.get('/venues/regions').then((r) => setRegions(r.data || [])).catch(() => {});
    api.get('/venues/trending-cities').then((r) => setTrending(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => fetchPage(true), hasTextQuery ? 300 : 0);
    return () => clearTimeout(handle);
  }, [fetchPage, hasTextQuery]);

  useEffect(() => {
    if (!hasTextQuery) {
      setPeople([]);
      return undefined;
    }
    const handle = setTimeout(() => {
      api.get('/users/search', { params: { q: trimmed } })
        .then((r) => setPeople(r.data || []))
        .catch(() => setPeople([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [hasTextQuery, trimmed]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading && !loadingMore) fetchPage(false);
    }, { rootMargin: '600px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore]);

  const useMyLocation = async () => {
    setLocating(true);
    try {
      const c = await getGeolocation();
      if (!c) {
        toast.error('Could not get your location. Enable GPS in browser settings.');
        return;
      }
      setCoords(c);
      setLocationLabel('Your location');
      setSearch('');
      toast.success('Showing venues near you');
    } finally {
      setLocating(false);
    }
  };

  const submitZip = async () => {
    if (!/^\d{5}$/.test(trimmed)) {
      toast.error('Enter a 5-digit US ZIP code');
      return;
    }
    setLocating(true);
    try {
      const r = await api.get(`/zip/${trimmed}`);
      setCoords({ lat: r.data.lat, lon: r.data.lon });
      setLocationLabel(`${r.data.city}, ${r.data.state_abbr} ${r.data.zip}`);
      setSearch('');
      toast.success(`Showing venues near ${r.data.city}, ${r.data.state_abbr}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ZIP lookup failed');
    } finally {
      setLocating(false);
    }
  };

  const onAddressPicked = (d) => {
    if (typeof d?.lat !== 'number' || typeof d?.lon !== 'number') {
      toast.error('Could not locate that address');
      return;
    }
    setCoords({ lat: d.lat, lon: d.lon });
    const label = d.city ? `${d.city}, ${d.state || ''}`.trim().replace(/,\s*$/, '') : (d.address || 'Selected address');
    setLocationLabel(label);
    setSearch('');
    toast.success(`Showing venues near ${label}`);
  };

  const clearLocation = () => {
    setCoords(null);
    setLocationLabel('');
    setAddressQuery('');
  };

  const pills = [{ slug: 'all', label: 'All' }, ...CATEGORY_ORDER.map((slug) => ({ slug, label: CATEGORY_META[slug].label }))];
  const subtitle = loading
    ? 'Finding spots\u2026'
    : coords
      ? `${venues.length} spots near ${locationLabel || 'you'}`
      : `${venues.length} ${venues.length === 1 ? 'spot' : 'spots'} loaded${hasMore ? ' \u00b7 scroll for more' : ''}`;

  return (
    <div className="min-h-screen">
      <main className="mx-auto max-w-2xl px-4 pb-8 pt-6">
        <h1 className="font-display text-4xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Discover</h1>
        <p className="mt-1 text-sm text-white/60" data-testid="discover-count-subtitle">{subtitle}</p>

        {trending.length > 0 && !hasTextQuery && (
          <div className="mt-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/50">
              <Flame className="h-3.5 w-3.5 text-[#F6B24A]" /> Trending Cities this week
            </p>
            <div
              className="mt-2 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="discover-trending-cities"
            >
              {trending.map((c) => {
                const active = country === c.country && city === c.city;
                return (
                  <button
                    key={`${c.city}-${c.country}`}
                    onClick={() => {
                      if (active) { setCountry('all'); setCity('all'); return; }
                      setCountry(c.country);
                      setCity(c.city);
                    }}
                    className={`flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2.5 text-left transition-colors ${
                      active
                        ? 'border-[#F6B24A]/60 bg-[#F6B24A]/15'
                        : 'border-white/10 bg-white/5 hover:border-[#F6B24A]/40'
                    }`}
                    data-testid={`discover-trending-city-${c.city.replace(/\s+/g, '-')}`}
                  >
                    {c.is_hot && <Flame className="h-4 w-4 shrink-0 text-[#F6B24A]" />}
                    <span>
                      <span className="block text-sm font-semibold text-white">{c.city}</span>
                      <span className="block text-[11px] text-white/50">
                        {c.is_hot
                          ? `${c.checkins + c.posts} check-in${c.checkins + c.posts === 1 ? '' : 's'} & posts`
                          : `${c.country}${c.venues ? ` \u00b7 ${c.venues} spots` : ''}`}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/50" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && isZipLike) { e.preventDefault(); submitZip(); } }}
              placeholder="Search bars, clubs, cities, people..."
              className="h-13 rounded-2xl border-white/12 bg-white/5 pl-11 pr-20 text-base text-white placeholder:text-white/40"
              style={{ height: 52 }}
              data-testid="discover-search-input"
            />
            {isZipLike ? (
              <Button
                onClick={submitZip}
                disabled={locating}
                className="absolute right-1.5 top-1/2 h-9 -translate-y-1/2 rounded-xl bg-[#F6B24A] px-3 font-semibold text-[#1A1208] hover:bg-[#FFC065]"
                data-testid="discover-zip-go-button"
              >
                {locating ? <Loader2 className="h-4 w-4 th-spin" /> : 'Go'}
              </Button>
            ) : search ? (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/60 hover:bg-white/10"
                data-testid="discover-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Button
            onClick={useMyLocation}
            disabled={locating}
            variant="outline"
            className="h-10 shrink-0 rounded-2xl border-white/12 bg-white/5 px-3 text-xs text-white hover:bg-white/10"
            data-testid="discover-use-my-location-button"
          >
            {locating ? <Loader2 className="mr-1.5 h-3 w-3 th-spin" /> : <LocateFixed className="mr-1.5 h-3 w-3" />}
            Near Me
          </Button>
          <div className="relative flex-1">
            <MapPin className="pointer-events-none absolute left-3 top-[20px] z-10 h-4 w-4 -translate-y-1/2 text-white/50" />
            <AddressAutocompleteInput
              value={addressQuery}
              onChange={setAddressQuery}
              onSelect={onAddressPicked}
              placeholder="Search near any US address…"
              className="h-10 rounded-2xl border-white/12 bg-white/5 pl-9 text-sm text-white placeholder:text-white/40"
              testId="discover-address-search-input"
            />
          </div>
        </div>

        {locationLabel && (
          <div className="mt-3">
            <span className="inline-flex items-center gap-1 rounded-full border border-[#F6B24A]/30 bg-[#F6B24A]/10 px-3 py-1 text-xs text-[#F6B24A]" data-testid="discover-active-location">
              <MapPin className="h-3 w-3" /> {locationLabel}
              <button onClick={clearLocation} className="ml-1 rounded-full p-0.5 hover:bg-white/10" data-testid="discover-clear-location"><X className="h-3 w-3" /></button>
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => setOpenNow((v) => !v)}
            className={`flex h-10 shrink-0 items-center gap-1.5 rounded-2xl border px-3 text-xs font-semibold transition-colors ${
              openNow
                ? 'border-[#F6B24A]/60 bg-[#F6B24A]/15 text-[#F6B24A]'
                : 'border-white/12 bg-white/5 text-white/80 hover:bg-white/10'
            }`}
            data-testid="discover-open-now-toggle"
          >
            <Clock className="h-3.5 w-3.5" /> Open Now
          </button>
          <Select value={country} onValueChange={(v) => { setCountry(v); setCity('all'); }}>
            <SelectTrigger className="h-10 flex-1 rounded-2xl border-white/12 bg-white/5 text-xs text-white" data-testid="discover-country-select">
              <span className="flex items-center gap-1.5 truncate"><Globe className="h-3.5 w-3.5 shrink-0 text-white/50" /><SelectValue placeholder="All countries" /></span>
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="discover-country-option-all">All countries</SelectItem>
              {regions.map((r) => (
                <SelectItem key={r.country} value={r.country} data-testid={`discover-country-option-${r.country.replace(/\s+/g, '-')}`}>
                  {r.country} ({r.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {country !== 'all' && (
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="h-10 flex-1 rounded-2xl border-white/12 bg-white/5 text-xs text-white" data-testid="discover-city-select">
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all" data-testid="discover-city-option-all">All cities</SelectItem>
                {(regions.find((r) => r.country === country)?.cities || []).map((c) => (
                  <SelectItem key={c.city} value={c.city} data-testid={`discover-city-option-${c.city.replace(/\s+/g, '-')}`}>
                    {c.city} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div
          className="mt-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="discover-category-pills"
        >
          {pills.map((p) => {
            const active = category === p.slug;
            return (
              <button
                key={p.slug}
                onClick={() => setCategory(p.slug)}
                className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? 'border-[#F6B24A]/60 bg-[#F6B24A]/15 text-[#F6B24A]'
                    : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
                }`}
                data-testid={`discover-category-${p.slug}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {hasTextQuery && people.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50">People</p>
            <GlassCard className="mt-2 divide-y divide-white/5" data-testid="discover-people-results">
              {people.map((u) => (
                <div key={u.id} className="flex w-full items-center gap-3 p-3 transition-colors hover:bg-white/5" data-testid={`discover-person-row-${u.id}`}>
                  <button onClick={() => nav(`/user/${u.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`discover-person-link-${u.id}`}>
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.display_name} className="h-10 w-10 shrink-0 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F6B24A]/15 text-sm font-bold text-[#F6B24A]">
                        {(u.display_name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-white">{u.display_name}</span>
                      <span className="block text-xs capitalize text-white/50">{u.role === 'venue_owner' ? 'Venue owner' : u.role || 'member'}</span>
                    </span>
                  </button>
                  {me?.id !== u.id && (
                    <Button
                      onClick={() => toggleFollow(u)}
                      disabled={followBusy === u.id}
                      variant="outline"
                      className={`h-8 shrink-0 rounded-full px-4 text-xs font-semibold ${
                        u.is_following
                          ? 'border-white/15 bg-white/5 text-white/70 hover:bg-white/10'
                          : 'border-[#F6B24A]/50 bg-[#F6B24A]/15 text-[#F6B24A] hover:bg-[#F6B24A]/25'
                      }`}
                      data-testid={`discover-person-follow-${u.id}`}
                    >
                      {followBusy === u.id ? <Loader2 className="h-3.5 w-3.5 th-spin" /> : u.is_following ? 'Following' : 'Follow'}
                    </Button>
                  )}
                </div>
              ))}
            </GlassCard>
          </div>
        )}

        <div className="mt-4 space-y-3" data-testid="discover-venue-list">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-white/60">
              <Loader2 className="h-4 w-4 th-spin" /> Finding venues…
            </div>
          )}
          {!loading && venues.length === 0 && (
            <GlassCard className="p-6 text-center text-white/60" data-testid="discover-empty">
              No spots found{hasTextQuery ? ` for \u201c${trimmed}\u201d` : ''}. Try a different search or category.
            </GlassCard>
          )}
          {!loading && venues.map((v) => (
            <VenueRow key={v.id} venue={v} onClick={() => nav(`/venue/${v.id}`)} />
          ))}
          <div ref={sentinelRef} />
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 py-4 text-white/60">
              <Loader2 className="h-4 w-4 th-spin" /> Loading more…
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
